<template>
  <div class="tour" :style="style">
    <e-icon name="lightBulb" class="text-blue" />
    <div class="ml-3 mt-1 mr-4">
      <div class="paragraph-m mb-4">
        <b>{{ title }}</b>
      </div>

      <div class="text-secondary paragraph-s_h">
        <slot name="text" />
      </div>
    </div>

    <e-icon name="closeSvg" size="16" class="tour__close" @click="close" />

    <slot v-if="!$vuetify.display.smAndDown" name="arrow" />
  </div>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { onMounted, ref, toRef } from 'vue';
import { EIcon } from '@/core/components/ui';
import { useTourStore } from '@/core/store/tour';

type VerticalPosition = 'top' | 'bottom';
type HorizontalPosition = 'left' | 'right';

const props = defineProps<{
  rootElementSelector?: string;
  location?: `${VerticalPosition} ${HorizontalPosition}`;
  title: string;
  offsetTop?: number;
  offsetLeft?: number;
  maxWidth?: string;
  static?: boolean;
}>();

defineSlots<{
  text: () => void;
  arrow: () => void;
}>();

const emit = defineEmits<{
  (event: 'close'): void;
}>();

const { closeActiveTour } = useTourStore();

const close = () => {
  closeActiveTour();
  emit('close');
};

const rootPosition = ref({
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
});

const style = toRef(() => ({
  position: props.static ? 'static' : props.location ? 'fixed' : ('absolute' as 'fixed' | 'absolute' | 'static'),
  top: rootPosition.value.top ? `${rootPosition.value.top + (props.offsetTop || 0)}px` : 'auto',
  left: rootPosition.value.left ? `${rootPosition.value.left + (props.offsetLeft || 0)}px` : 'auto',
  right: rootPosition.value.right ? `${rootPosition.value.right + (props.offsetLeft || 0)}px` : 'auto',
  bottom: rootPosition.value.bottom ? `${rootPosition.value.bottom + (props.offsetLeft || 0)}px` : 'auto',
  maxWidth: props.maxWidth,
}));

const locationOffset = 24;

const root = ref<HTMLElement>();

const calcRootPosition = async () => {
  let top = 0;
  let right = 0;
  let bottom = 0;
  let left = 0;

  if (props.rootElementSelector) {
    const rootEl = document.querySelector(props.rootElementSelector);

    if (!rootEl) {
      return;
    }

    root.value = document.getElementById('app') as HTMLElement;

    const rootRect = rootEl.getBoundingClientRect();

    top = rootRect.top;
    left = rootRect.left;
  }

  if (props.location) {
    const loc = props.location.split(' ');

    if (loc[0] === 'bottom') {
      bottom = locationOffset;
    }

    if (loc[1] === 'right') {
      right = locationOffset;
    }

    if (loc[1] === 'left') {
      left = locationOffset;
    }
  }

  rootPosition.value = {
    top,
    left,
    right,
    bottom,
  };
};

useEventListener('transitionend', () => {
  calcRootPosition();
});

onMounted(calcRootPosition);
</script>

<style scoped lang="scss">
.tour {
  position: absolute;
  z-index: $z-index-4;

  display: flex;

  max-width: 560px;
  padding: $gap-4;

  background-color: $main-bg-color;
  border: 1px solid $primary-color;
  border-radius: $border-radius;

  transition:
    left $transition-duration,
    top $transition-duration;

  &__close {
    margin-left: auto;

    color: $text-secondary-color;

    cursor: pointer;
  }

  @include sm-and-down {
    top: $gap-4 !important;
    right: $gap-5 !important;
    bottom: auto !important;
    left: auto !important;

    max-width: calc(100% - $gap-5 * 2);
  }

  @include xs {
    right: $gap-4 !important;

    max-width: calc(100% - $gap-4 * 2);
  }
}
</style>
